import * as React from "react";

import { cn } from "./cn";

type DeltaGoodWhen = "up" | "down";
type DeltaTone = "good" | "bad" | "flat";

type MetricDelta = {
  pct: number | null;
  /** Default "vs prior period". */
  label?: string;
  goodWhen: DeltaGoodWhen;
};

/** Direction x which direction is good. Zero and unknown are flat. */
function deltaTone(pct: number | null, goodWhen: DeltaGoodWhen): DeltaTone {
  if (pct === null || pct === 0 || !Number.isFinite(pct)) return "flat";
  const up = pct > 0;
  return up === (goodWhen === "up") ? "good" : "bad";
}

/** "+367.6%", "−48.7%" (U+2212), "0.0%"; null when there is no figure. */
function formatDelta(pct: number | null): string | null {
  if (pct === null || !Number.isFinite(pct)) return null;
  const abs = Math.abs(pct).toFixed(1);
  if (pct > 0) return `+${abs}%`;
  if (pct < 0) return `−${abs}%`;
  return `${abs}%`;
}

const toneClass: Record<DeltaTone, string> = {
  good: "text-success",
  bad: "text-danger",
  flat: "text-muted-foreground",
};

type MetricBlockProps = {
  label: string;
  value: string;
  delta?: MetricDelta;
  sub?: React.ReactNode;
  size?: "hero" | "default";
  /** E.g. an arrow link at the top right. */
  action?: React.ReactNode;
  className?: string;
};

function MetricBlock({
  label,
  value,
  delta,
  sub,
  size = "default",
  action,
  className,
}: MetricBlockProps) {
  const text = delta ? formatDelta(delta.pct) : null;
  const tone = delta ? deltaTone(delta.pct, delta.goodWhen) : "flat";
  const arrow = delta?.pct && delta.pct > 0 ? "↑" : "↓";
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-muted-foreground">{label}</p>
        {action}
      </div>
      <p
        className={cn(
          "font-semibold text-foreground",
          size === "hero" ? "text-3xl leading-tight" : "text-xl leading-snug",
        )}
      >
        {value}
      </p>
      {delta ? (
        text === null ? (
          <p className="text-xs text-muted-foreground">No prior period</p>
        ) : (
          // The arrow and the figure sit in fixed slots so "vs prior period"
          // starts at the same x whatever the figure: no layout shift.
          <p className={cn("text-xs font-medium", toneClass[tone])}>
            <span
              data-slot="metric-delta-arrow"
              aria-hidden="true"
              className={cn(
                "inline-block w-[1.5ch]",
                delta.pct === 0 && "invisible",
              )}
            >
              {arrow}
            </span>
            <span
              data-slot="metric-delta-figure"
              className="inline-block min-w-[8ch] tabular-nums"
            >
              {text}
            </span>{" "}
            <span className="font-normal text-muted-foreground">
              {delta.label ?? "vs prior period"}
            </span>
          </p>
        )
      ) : null}
      {sub ? <p className="text-xs text-muted-foreground">{sub}</p> : null}
    </div>
  );
}

type MetricRowProps = {
  label: string;
  value: string;
  hint?: string;
  className?: string;
};

function MetricRow({ label, value, hint, className }: MetricRowProps) {
  const hintId = React.useId();
  return (
    <div
      data-slot="metric-row"
      className={cn(
        "flex items-baseline justify-between gap-3 py-2 not-first:border-t not-first:border-border",
        className,
      )}
    >
      <span
        className={cn(
          "type-small text-muted-foreground",
          hint && "underline decoration-dotted underline-offset-4",
        )}
        title={hint}
        aria-describedby={hint ? hintId : undefined}
      >
        {label}
      </span>
      {hint ? (
        <span id={hintId} className="sr-only">
          {hint}
        </span>
      ) : null}
      {/* A box that starts right after the label, the figure right-aligned
          inside it: a new figure never moves the box (no layout shift). */}
      <span className="type-figure type-small min-w-0 flex-1 text-right font-medium text-foreground tabular-nums">
        {value}
      </span>
    </div>
  );
}

export { deltaTone, formatDelta, MetricBlock, MetricRow };
export type {
  DeltaGoodWhen,
  DeltaTone,
  MetricBlockProps,
  MetricDelta,
  MetricRowProps,
};
